import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import CommonDatePicker from '../../../components/DatePicker/CommonDatePicker';
import OrgSelect from '../../../components/OrgSelect/OrgSelect';

const parseDateOnly = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.substring(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const formatDateLabel = (date) => {
  if (!date) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

function TaskCreateModal({ show, onHide, onSubmit, categoryId, categoryName, project }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'pending',
    progress_rate: 0,
    start_date: null,
    end_date: null,
    sort_order: 10
  });

  const [assignees, setAssignees] = useState([]);
  const [ccList, setCcList] = useState([]);

  const [showOrgSelect, setShowOrgSelect] = useState(false);
  const [orgSelectTarget, setOrgSelectTarget] = useState('assignee'); // 'assignee' or 'cc'

  const projectMinDate = useMemo(() => parseDateOnly(project?.start_date), [project?.start_date]);
  const projectMaxDate = useMemo(() => parseDateOnly(project?.end_date), [project?.end_date]);

  const popoverRef = useRef(null);
  const [showMemberPopover, setShowMemberPopover] = useState(false);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [tempAssignees, setTempAssignees] = useState([]);

  const getRandomColor = (name) => {
    if (!name) return '#6c757d';
    // 우리나라 정서에 맞게 빨간색(red) 계열 완전 배제
    const colors = ['#2196F3', '#9C27B0', '#FF9800', '#4CAF50', '#00BCD4', '#3F51B5', '#009688', '#795548', '#607D8B', '#0288D1', '#8E24AA', '#F57C00', '#388E3C'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(Math.imul(hash, 2654435761));
    return colors[hash % colors.length];
  };

  const projectUsers = useMemo(() => {
    if (!project) return [];
    const users = [];
    const addIfUnique = (u) => {
      if (u && u.id && !users.find(x => String(x.id) === String(u.id))) {
        users.push(u);
      }
    };
    addIfUnique(project.author);
    addIfUnique(project.reviewer);
    addIfUnique(project.leader);
    if (project.members && Array.isArray(project.members)) {
      project.members.forEach(m => addIfUnique(m.user));
    }
    return users;
  }, [project]);

  const filteredProjectUsers = useMemo(() => {
    let result = projectUsers;
    if (memberSearchTerm) {
      result = projectUsers.filter(u => u.name?.includes(memberSearchTerm));
    }
    return [...result].sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  }, [projectUsers, memberSearchTerm]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setShowMemberPopover(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleTempAssigneeSelection = (user) => {
    const uid = String(user.userid || user.id);
    const exists = tempAssignees.find(m => String(m.target_id) === uid);

    if (exists) {
      setTempAssignees(prev => prev.filter(m => String(m.target_id) !== uid));
    } else {
      setTempAssignees(prev => [...prev, {
        target_type: 'user',
        target_id: uid,
        target_name: user.name,
        disp_target_name: `${user.name} / ${user.org?.position || user.position || ''} ${user.org?.dept_name ? '(' + user.org.dept_name + ')' : ''}`
      }]);
    }
  };

  useEffect(() => {
    if (show) {
      setFormData({
        title: '',
        description: '',
        status: 'pending',
        progress_rate: 0,
        start_date: null,
        end_date: null,
        sort_order: 10
      });
      setAssignees([]);
      setCcList([]);
    }
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      let nextStatus = prev.status;
      let nextProgress = prev.progress_rate;

      if (name === 'status') {
        nextStatus = value;
        if (value === 'completed') {
          nextProgress = 100;
        } else if (prev.status === 'completed' && Number(prev.progress_rate) === 100) {
          nextProgress = value === 'pending' ? 0 : 50;
        }
      } else if (name === 'progress_rate') {
        const val = parseInt(value, 10);
        nextProgress = val;
        if (val === 100) {
          nextStatus = 'completed';
        } else if (prev.status === 'completed' && val < 100) {
          nextStatus = val === 0 ? 'pending' : 'in_progress';
        }
      } else {
        return { ...prev, [name]: value };
      }

      return { ...prev, [name]: value, status: nextStatus, progress_rate: nextProgress };
    });
  };

  const handleSubmit = () => {
    if (!formData.title.trim()) {
      alert('세부 업무 제목을 입력해주세요.');
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      alert('시작일자와 완료(목표)일자를 모두 선택해주세요.');
      return;
    }
    if (projectMinDate && formData.start_date < projectMinDate) {
      alert(`시작일자는 프로젝트 진행기간(${formatDateLabel(projectMinDate)} ~ ${formatDateLabel(projectMaxDate)}) 안에서 선택해주세요.`);
      return;
    }
    if (projectMaxDate && formData.start_date > projectMaxDate) {
      alert(`시작일자는 프로젝트 진행기간(${formatDateLabel(projectMinDate)} ~ ${formatDateLabel(projectMaxDate)}) 안에서 선택해주세요.`);
      return;
    }
    if (projectMinDate && formData.end_date < projectMinDate) {
      alert(`완료(목표)일자는 프로젝트 진행기간(${formatDateLabel(projectMinDate)} ~ ${formatDateLabel(projectMaxDate)}) 안에서 선택해주세요.`);
      return;
    }
    if (projectMaxDate && formData.end_date > projectMaxDate) {
      alert(`완료(목표)일자는 프로젝트 진행기간(${formatDateLabel(projectMinDate)} ~ ${formatDateLabel(projectMaxDate)}) 안에서 선택해주세요.`);
      return;
    }
    if (formData.end_date < formData.start_date) {
      alert('완료(목표)일자는 시작일자 이후여야 합니다.');
      return;
    }
    if (assignees.length === 0) {
      alert('담당자를 최소 1명 이상 지정해주세요.');
      return;
    }

    const formatDate = (date) => date ? new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().split('T')[0] : null;

    const mappedMembers = [
      ...assignees.map(m => ({ user_id: m.target_id, is_manager: true })),
      ...ccList.map(m => ({ user_id: m.target_id, is_manager: false }))
    ];

    let finalStatus = formData.status;
    let finalProgress = parseInt(formData.progress_rate) || 0;
    if (finalStatus === 'completed') finalProgress = 100;
    else if (finalProgress === 100) finalStatus = 'completed';

    onSubmit({
      ...formData,
      status: finalStatus,
      category_id: categoryId,
      progress_rate: finalProgress,
      start_date: formatDate(formData.start_date),
      end_date: formatDate(formData.end_date),
      members: mappedMembers
    });
  };

  const handleRemoveAssignee = (id) => {
    setAssignees(prev => prev.filter(m => m.target_id !== id));
  };

  const handleRemoveCc = (id) => {
    setCcList(prev => prev.filter(m => m.target_id !== id));
  };

  if (!show) return null;

  return ReactDOM.createPortal(
    <>
      <div className="modal-backdrop fade show" style={{ opacity: 0.6, zIndex: 9998, position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}></div>
      <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.2)', zIndex: 9999, position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', overflowY: 'auto' }}>
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px', overflow: 'hidden' }}>

            <div className="modal-header bg-light border-bottom pt-3 px-4 pb-3">
              <h4 className="modal-title fw-bolder text-dark">
                <i className="fas fa-layer-group me-2 text-primary"></i>신규 세부 업무 등록
              </h4>
              <button type="button" className="btn-close" onClick={onHide}></button>
            </div>

            <div className="modal-body px-4 pt-4 pb-3 bg-white">
              <div className="row g-4">
                {/* 좌측 영역: 제목, 설명 */}
                <div className="col-md-7 border-end pe-4 d-flex flex-column">
                  <div className="mb-4 p-4 bg-light rounded-3 border border-dashed">
                    {categoryName && (
                      <div className="mb-4">
                        <label className="form-label fw-bolder text-dark mb-3">
                          <i className="fas fa-layer-group text-primary me-2"></i>분류
                        </label>
                        <div className="bg-white border-0 shadow-sm p-3 fs-6 rounded text-dark">
                          {categoryName}
                        </div>
                      </div>
                    )}

                    <label className="form-label fw-bolder text-dark mb-3">
                      <i className="fas fa-heading text-primary me-2"></i>업무 제목 <span className="text-danger">*</span>
                    </label>
                    <input type="text" className="form-control border-0 shadow-sm p-3 fs-6" style={{ backgroundColor: '#fff9e6' }} name="title" value={formData.title} onChange={handleChange} placeholder="수행할 세부 업무명을 입력하세요" />
                    <div className="form-text mt-3 text-muted" style={{ fontSize: '0.8rem' }}>
                      <i className="fas fa-info-circle me-1"></i>구체적인 작업 단위로 명확하게 적어주세요. (예: 메인 화면 UI 디자인, 결제 API 연동 등)
                    </div>
                  </div>
                  <div className="mb-0 p-4 bg-light rounded-3 border border-dashed flex-grow-1 d-flex flex-column">
                    <label className="form-label fw-bolder text-dark mb-3">
                      <i className="fas fa-align-left text-primary me-2"></i>상세 설명
                    </label>
                    <textarea className="form-control bg-white border-0 shadow-sm p-3 flex-grow-1" style={{ resize: 'none', minHeight: '200px' }} name="description" value={formData.description} onChange={handleChange} placeholder="세부 업무 내용을 상세히 작성해주세요"></textarea>
                  </div>
                </div>

                {/* 우측 영역: 기간, 상태, 완료율, 정렬순서, 담당자/참조자 */}
                <div className="col-md-5 ps-4">
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <div className="d-flex align-items-center mb-1">
                        <i className="fas fa-calendar-alt text-primary me-2"></i>
                        <label className="form-label fw-bold text-muted fs-7 mb-0">시작일자 <span className="text-danger">*</span></label>
                      </div>
                      <div className="position-relative pms-datepicker-container pms-datepicker-required">
                        <CommonDatePicker
                          portalId=""
                          value={formData.start_date}
                          onChange={(date) => setFormData(p => ({
                            ...p,
                            start_date: date,
                            end_date: p.end_date && date && p.end_date < date ? null : p.end_date
                          }))}
                          placeholder="시작일 선택"
                          minDate={projectMinDate}
                          maxDate={formData.end_date && projectMaxDate
                            ? (formData.end_date < projectMaxDate ? formData.end_date : projectMaxDate)
                            : projectMaxDate}
                          className="form-control border-0 shadow-none fw-bold text-dark pe-5 py-2 date-cursor-pointer"
                        />
                        <i className="fas fa-calendar-day text-muted position-absolute" style={{ right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}></i>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="d-flex align-items-center mb-1">
                        <i className="fas fa-calendar-check text-primary me-2"></i>
                        <label className="form-label fw-bold text-muted fs-7 mb-0">완료(목표)일자 <span className="text-danger">*</span></label>
                      </div>
                      <div className="position-relative pms-datepicker-container pms-datepicker-required">
                        <CommonDatePicker
                          portalId=""
                          value={formData.end_date}
                          onChange={(date) => setFormData(p => ({ ...p, end_date: date }))}
                          placeholder="완료일 선택"
                          minDate={formData.start_date && projectMinDate
                            ? (formData.start_date > projectMinDate ? formData.start_date : projectMinDate)
                            : projectMinDate}
                          maxDate={projectMaxDate}
                          className="form-control border-0 shadow-none fw-bold text-dark pe-5 py-2 date-cursor-pointer"
                        />
                        <i className="fas fa-calendar-day text-muted position-absolute" style={{ right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}></i>
                      </div>
                    </div>
                    {projectMinDate && projectMaxDate && (
                      <div className="col-12 mt-1">
                        <div className="form-text text-muted" style={{ fontSize: '0.75rem' }}>
                          <i className="fas fa-info-circle me-1"></i>
                          선택 가능 기간: {formatDateLabel(projectMinDate)} ~ {formatDateLabel(projectMaxDate)} (프로젝트 진행기간)
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <div className="d-flex align-items-center mb-1">
                        <i className="fas fa-tasks text-primary me-2"></i>
                        <label className="form-label fw-bold text-muted fs-7 mb-0">진행상태</label>
                      </div>
                      <select className="form-select bg-light border-0 shadow-none fw-bold" name="status" value={formData.status} onChange={handleChange}>
                        <option value="pending">⏳ 시작 전</option>
                        <option value="in_progress">🔄 진행 중</option>
                        <option value="completed">✅ 완료</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <div className="d-flex align-items-center mb-1">
                        <i className="fas fa-sort-numeric-down text-primary me-2"></i>
                        <label className="form-label fw-bold text-muted fs-7 mb-0">정렬순서</label>
                      </div>
                      <input type="number" className="form-control bg-light border-0 shadow-none" name="sort_order" value={formData.sort_order} onChange={handleChange} />
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <i className="fas fa-chart-line text-primary me-2"></i>
                      <label className="form-label fw-bold text-muted fs-7 mb-0">완료율 (%)</label>
                    </div>
                    <div className="d-flex align-items-center bg-light p-3 rounded">
                      <input type="range" className="custom-progress-range me-3" name="progress_rate" value={formData.progress_rate} onChange={handleChange} min="0" max="100" step="10" style={{ flexGrow: 1, background: `linear-gradient(to right, #4c6ef5 ${formData.progress_rate}%, #dee2e6 ${formData.progress_rate}%)` }} />
                      <style>{`
                        .custom-progress-range { -webkit-appearance: none; height: 8px; border-radius: 4px; outline: none; transition: background 0.2s; cursor: pointer; }
                        .custom-progress-range::-webkit-slider-thumb { -webkit-appearance: none; width: 18px; height: 18px; border-radius: 50%; background: #4c6ef5; border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.3); cursor: pointer; }
                        .date-cursor-pointer { cursor: pointer !important; }
                        /* PMS 달력 전용 스타일 격리 */
                        .pms-datepicker-container .react-datepicker-wrapper {
                          width: 100% !important;
                        }
                        .pms-datepicker-required input {
                          background-color: #fff9e6 !important;
                          border-radius: 0.375rem !important;
                        }
                      `}</style>
                      <span className="fw-bolder text-primary fs-5" style={{ width: '50px' }}>{formData.progress_rate}%</span>
                    </div>
                  </div>

                  {/* 담당자 지정 */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-user-tie text-primary me-2"></i>
                        <label className="form-label fw-bold text-muted fs-7 mb-0">담당자 <span className="text-danger">*</span></label>
                      </div>
                      <div className="position-relative" ref={popoverRef}>
                        <button className="btn btn-sm btn-light text-primary fw-bold py-1 px-2" onClick={() => {
                          if (showMemberPopover) {
                            setShowMemberPopover(false);
                          } else {
                            setTempAssignees([...assignees]);
                            setShowMemberPopover(true);
                          }
                        }}>
                          <i className="fas fa-plus me-1"></i>추가
                        </button>
                        {showMemberPopover && (
                          <div className="position-absolute shadow-lg bg-white rounded-3 p-0" style={{ top: '100%', right: 0, marginTop: '8px', width: '280px', zIndex: 1050, border: '1px solid #c2d7fa', overflow: 'hidden' }}>
                            <div className="p-2 border-bottom" style={{ backgroundColor: '#e8f0fe' }}>
                              <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0"><i className="fas fa-search text-muted"></i></span>
                                <input type="text" className={`form-control border-start-0 ps-0 shadow-none ${memberSearchTerm ? 'border-end-0' : ''}`} placeholder="담당자를 입력하세요" value={memberSearchTerm} onChange={(e) => setMemberSearchTerm(e.target.value)} />
                                {memberSearchTerm && (
                                  <span className="input-group-text bg-white border-start-0 cursor-pointer" onClick={() => setMemberSearchTerm('')} title="검색 초기화">
                                    <i className="fas fa-times-circle text-muted" style={{ opacity: 0.6 }}></i>
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="p-2" style={{ maxHeight: '140px', overflowY: 'auto' }}>
                              {filteredProjectUsers.length > 0 ? filteredProjectUsers.map(u => {
                                const uIdStr = String(u.userid || u.id);
                                const isSelected = tempAssignees.some(m => String(m.target_id) === uIdStr);
                                return (
                                  <div key={uIdStr} className={`d-flex align-items-center px-2 py-1 mb-0 rounded cursor-pointer ${isSelected ? '' : 'hover-bg-light'}`} style={{ backgroundColor: isSelected ? '#e8f0fe' : 'transparent', border: isSelected ? '1px solid #d2e3fc' : '1px solid transparent' }} onClick={() => toggleTempAssigneeSelection(u)}>
                                    <div className="flex-shrink-0 me-2">
                                      {isSelected ? (
                                        <div className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white shadow-sm" style={{ width: '24px', height: '24px' }}>
                                          <i className="fas fa-check" style={{ fontSize: '10px' }}></i>
                                        </div>
                                      ) : (
                                        <div className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm" style={{ width: '24px', height: '24px', backgroundColor: getRandomColor(u.name), fontSize: '11px' }}>
                                          {u.name?.charAt(0)}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-grow-1 min-w-0">
                                      <div className={`fw-bold fs-8 text-truncate ${isSelected ? 'text-primary' : 'text-dark'}`} title={`${u.name} ${u.org?.position || ''}${u.org?.dept_name ? `[${u.org.dept_name}]` : ''}`}>
                                        {u.name} {u.org?.position || ''}{u.org?.dept_name ? `[${u.org.dept_name}]` : ''}
                                      </div>
                                    </div>
                                    {isSelected && (
                                      <div className="flex-shrink-0 ms-2">
                                        <button className="btn btn-sm btn-link text-danger p-0 d-flex align-items-center justify-content-center" title="선택 해제" onClick={(e) => { e.stopPropagation(); toggleTempAssigneeSelection(u); }}>
                                          <i className="fas fa-times fs-8"></i>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              }) : (
                                <div className="text-center text-muted p-3 fs-8">지정 가능한 멤버가 없습니다.</div>
                              )}
                            </div>
                            <div className="p-2 border-top d-flex justify-content-between align-items-center" style={{ backgroundColor: '#e8f0fe' }}>
                              <span className="fs-8 text-muted fw-bold">선택된 수: <strong className="text-primary ms-1">{tempAssignees.length}</strong></span>
                              <button className="btn btn-sm btn-primary py-1 px-3 fs-8 fw-bold" onClick={() => { setAssignees(tempAssignees); setShowMemberPopover(false); }}>선택 완료</button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="border border-dashed rounded p-2" style={{ backgroundColor: '#fff9e6', minHeight: '55px', maxHeight: '120px', overflowY: 'auto' }}>
                      {assignees.length > 0 ? (
                        <div className="d-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                          {assignees.map(m => (
                            <span key={m.target_id} className="badge bg-white text-dark border shadow-sm px-2 py-2 d-flex align-items-center justify-content-between" style={{ fontSize: '0.75rem', fontWeight: '500', minWidth: 0 }} title={m.disp_target_name || m.target_name}>
                              <span className="d-flex align-items-center" style={{ minWidth: 0, flex: 1 }}>
                                <i className="fas fa-user text-secondary me-2 flex-shrink-0"></i>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {m.disp_target_name || m.target_name}
                                </span>
                              </span>
                              <button type="button" className="btn-close btn-close-sm ms-2 flex-shrink-0" style={{ fontSize: '0.5rem' }} onClick={() => handleRemoveAssignee(m.target_id)}></button>
                            </span>
                          ))}
                        </div>
                      ) : <div className="text-muted small p-1">지정된 담당자가 없습니다.</div>}
                    </div>
                  </div>

                  {/* 참조자 지정 */}
                  <div className="mb-0">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-share-square text-info me-2"></i>
                        <label className="form-label fw-bold text-muted fs-7 mb-0">참조자</label>
                      </div>
                      <button className="btn btn-sm btn-light text-secondary fw-bold py-1 px-2" onClick={() => { setOrgSelectTarget('cc'); setShowOrgSelect(true); }}>
                        <i className="fas fa-plus me-1"></i>추가
                      </button>
                    </div>
                    <div className="border border-dashed rounded p-2 bg-light" style={{ minHeight: '55px', maxHeight: '120px', overflowY: 'auto' }}>
                      {ccList.length > 0 ? (
                        <div className="d-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                          {ccList.map(m => (
                            <span key={m.target_id} className="badge bg-white text-dark border shadow-sm px-2 py-2 d-flex align-items-center justify-content-between" style={{ fontSize: '0.75rem', fontWeight: '500', minWidth: 0 }} title={m.disp_target_name || m.target_name}>
                              <span className="d-flex align-items-center" style={{ minWidth: 0, flex: 1 }}>
                                <i className="fas fa-user text-secondary me-2 flex-shrink-0"></i>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {m.disp_target_name || m.target_name}
                                </span>
                              </span>
                              <button type="button" className="btn-close btn-close-sm ms-2 flex-shrink-0" style={{ fontSize: '0.5rem' }} onClick={() => handleRemoveCc(m.target_id)}></button>
                            </span>
                          ))}
                        </div>
                      ) : <div className="text-muted small p-1">지정된 참조자가 없습니다.</div>}
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <div className="modal-footer bg-light border-top py-2 px-3">
              <button type="button" className="btn btn-outline-secondary fw-bold px-3" onClick={onHide}>취소</button>
              <button type="button" className="btn btn-primary fw-bold px-3 shadow-sm" onClick={handleSubmit}>
                <i className="fas fa-check me-2"></i>업무 등록
              </button>
            </div>

          </div>
        </div>
      </div>

      {showOrgSelect && (
        <OrgSelect
          selectionMode="multiple"
          targetType="user"
          initialSelected={orgSelectTarget === 'assignee' ? assignees : ccList}
          onConfirm={(targets) => {
            if (orgSelectTarget === 'assignee') setAssignees(targets);
            else setCcList(targets);
            setShowOrgSelect(false);
          }}
          onCancel={() => setShowOrgSelect(false)}
        />
      )}
    </>,
    document.body
  );
}

export default TaskCreateModal;
